import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  PropsWithChildren,
} from 'react';
import { useSearchParams } from 'react-router';
import { ChevronDown, LayoutDashboard, Map as MapIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { SECTOR, type AORHierarchyNode } from './utils/schema';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { GetSitrepsResponse } from 'components/Reports/reports.types';
import CurrentReportsSitrepCard from 'components/Reports/CurrentReportsSitrepCard';
import { useAorSitrep } from 'components/DisasterGPT/hooks/useSitrepsV2';
import {
  SITSTAT_AOR_PARAM,
  SITSTAT_OPEN_TAB,
  SITSTAT_TEMPLATE_PARAM,
} from '../SitStatTab/sitstat-navigation';
import { SITSTAT_AOR_VIEW_TEMPLATE_ID } from '../SitStatTab/sitstat-system-templates';
import { useUscgFocRequirementsFlag } from 'utils/featureFlags';
import { getUscgFocAorAssets } from './utils/uscgFocAorAssets';
import { useAutoZoomToHomeAor } from './utils/useAutoZoomToHomeAor';

type HierarchyItemCardActionsProps = {
  name: string;
  onGoTo: (e: MouseEvent) => void;
  tour?: string;
};

const HierarchyItemCardActions = ({
  name,
  onGoTo,
  tour,
}: HierarchyItemCardActionsProps) => (
  <Button
    type="button"
    variant="ghost"
    size="icon-sm"
    data-tour={tour}
    title="Go to location"
    aria-label={`Go to ${name} location`}
    onClick={onGoTo}
  >
    <MapIcon className="tw:h-4 tw:w-4" />
  </Button>
);

type HierarchyItemCardBodyProps = {
  aorId: string;
  aorName: string;
  aorSitrep: GetSitrepsResponse | null;
  onViewSitStat: () => void;
  onAction?: () => void;
  actionLabel?: string;
  noActionMessage?: string;
  actionTour?: string;
  /** Tour forces the OPCON assets list open and marks it for highlight. */
  forceAssetsOpen?: boolean;
  children?: React.ReactNode;
};

const HierarchyItemCardBody = ({
  aorId,
  aorName,
  aorSitrep,
  onViewSitStat,
  onAction,
  actionLabel,
  noActionMessage,
  actionTour,
  forceAssetsOpen = false,
  children,
}: PropsWithChildren<HierarchyItemCardBodyProps>) => {
  const [sitrepOpen, setSitrepOpen] = useState(false);
  const [userAssetsOpen, setUserAssetsOpen] = useState(false);
  const focRequirements = useUscgFocRequirementsFlag();
  const assetsOpen = forceAssetsOpen || userAssetsOpen;

  useEffect(() => {
    if (forceAssetsOpen) setUserAssetsOpen(true);
  }, [forceAssetsOpen]);

  const assets = useMemo(
    () => (focRequirements ? getUscgFocAorAssets(aorId, aorName) : []),
    [aorId, aorName, focRequirements]
  );

  return (
    <>
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        {aorSitrep && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-expanded={sitrepOpen}
            onClick={() => setSitrepOpen((prev) => !prev)}
          >
            {sitrepOpen ? 'Hide SITREP' : 'Show SITREP'}
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onViewSitStat}
        >
          <LayoutDashboard className="tw:h-4 tw:w-4" />
          View SitStat
        </Button>
        {focRequirements && assets.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-expanded={assetsOpen}
            onClick={() => setUserAssetsOpen((prev) => !prev)}
          >
            {assetsOpen ? 'Hide Assets' : 'View Assets'}
          </Button>
        )}
        {onAction && actionLabel && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-tour={actionTour}
            onClick={onAction}
          >
            {actionLabel}
          </Button>
        )}
      </div>
      {aorSitrep && (
        <Collapsible open={sitrepOpen} onOpenChange={setSitrepOpen}>
          <CollapsibleContent className="tw:min-w-0 tw:overflow-hidden">
            <CurrentReportsSitrepCard sitrep={aorSitrep} />
          </CollapsibleContent>
        </Collapsible>
      )}
      {focRequirements && assetsOpen && (
        <ul
          className="tw:space-y-2"
          data-tour={forceAssetsOpen ? 'aor-opcon-assets' : undefined}
          aria-label={`OPCON assets for ${aorName}`}
        >
          {assets.map((asset) => (
            <li
              key={asset.id}
              className="tw:flex tw:items-start tw:justify-between tw:gap-2 tw:rounded-md tw:border tw:border-border tw:bg-muted/20 tw:px-2.5 tw:py-2"
            >
              <div className="tw:min-w-0">
                <div className="tw:truncate tw:text-sm tw:font-medium">
                  {asset.name}
                </div>
                <div className="tw:text-xs tw:text-muted-foreground">
                  {asset.assetType} · OPCON {asset.opconUnit}
                </div>
                <div className="tw:text-xs tw:text-muted-foreground">
                  Home unit: {asset.homeUnit}
                </div>
              </div>
              <Badge variant="outline" className="tw:shrink-0">
                ALMIS
              </Badge>
            </li>
          ))}
        </ul>
      )}
      {!onAction && noActionMessage && (
        <div className="tw:text-xs tw:text-muted-foreground">
          {noActionMessage}
        </div>
      )}
      {children}
    </>
  );
};

type HierarchyItemCardProps = {
  item: AORHierarchyNode;
  sectionKey: string;
  allAorIds: string[];
  level?: string;
  /** IDs of all descendant AOR items (children, grandchildren, etc.) */
  descendantIds?: string[];
  onAction?: () => void;
  actionLabel?: string;
  noActionMessage?: string;
  /** `data-tour` on the card. Only the tour sets this. */
  cardTour?: string;
  /** `data-tour` on the expanded body. */
  detailsTour?: string;
  /** `data-tour` on the action button inside the body. */
  actionTour?: string;
  /** `data-tour` on the header's go-to-map button. */
  mapTour?: string;
  /** Opens the card while the tour is on this step, then closes it again. */
  forceOpen?: boolean;
  /** Tour forces View Assets open and tags the list for FOC-ORG-001e. */
  forceAssetsOpen?: boolean;
  marginClass?: string;
};

export const HierarchyItemCard = ({
  item,
  allAorIds,
  level,
  descendantIds,
  onAction,
  actionLabel,
  noActionMessage,
  cardTour,
  detailsTour,
  actionTour,
  mapTour,
  forceOpen = false,
  forceAssetsOpen = false,
  marginClass = 'tw:mt-2 tw:first:mt-0',
  children,
}: PropsWithChildren<HierarchyItemCardProps>) => {
  const [userOpen, setUserOpen] = useState(false);
  const open = forceOpen || userOpen;
  const setOpen = (next: boolean | ((prev: boolean) => boolean)) => {
    setUserOpen((prev) =>
      typeof next === 'function' ? next(forceOpen || prev) : next
    );
  };
  const {
    aorFilterIds,
    setAorFilterIds,
    isAorVisible,
    toggleAorVisibility,
    isMapVisible,
  } = useUscgMapContext();
  const { zoomToAorById } = useAutoZoomToHomeAor();
  const [, setSearchParams] = useSearchParams();

  const selfAndDescendantIds = useMemo(
    () => [item.id, ...(descendantIds ?? [])],
    [item.id, descendantIds]
  );

  const isIdVisible = useCallback(
    (id: string) => {
      if (!isAorVisible) return false;
      if (!aorFilterIds || aorFilterIds.length === 0) return true;
      return aorFilterIds.includes(id);
    },
    [aorFilterIds, isAorVisible]
  );

  const isAllVisible = useMemo(
    () => selfAndDescendantIds.every(isIdVisible),
    [selfAndDescendantIds, isIdVisible]
  );

  const isIndeterminate = useMemo(
    () => !isAllVisible && selfAndDescendantIds.some(isIdVisible),
    [isAllVisible, selfAndDescendantIds, isIdVisible]
  );

  const itemName = useMemo(
    () => (level && level === SECTOR ? `Sector ${item.name}` : item.name),
    [item.name, level]
  );

  const { data: aorSitrep } = useAorSitrep(item.id);

  const handleViewSitStat = useCallback(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('openTab', SITSTAT_OPEN_TAB);
      params.set(SITSTAT_TEMPLATE_PARAM, SITSTAT_AOR_VIEW_TEMPLATE_ID);
      params.set(SITSTAT_AOR_PARAM, item.id);
      return params;
    });
  }, [item.id, setSearchParams]);

  const handleToggleVisibility = useCallback(
    (e?: MouseEvent) => {
      e?.stopPropagation();

      if (isAllVisible) {
        const currentlyVisible =
          aorFilterIds && aorFilterIds.length > 0 ? aorFilterIds : allAorIds;
        const nextVisibleIds = currentlyVisible.filter(
          (id) => !selfAndDescendantIds.includes(id)
        );

        if (nextVisibleIds.length === 0) {
          setAorFilterIds([]);
          if (isAorVisible) toggleAorVisibility();
          return;
        }

        setAorFilterIds(nextVisibleIds);
        return;
      }

      if (!isAorVisible) {
        toggleAorVisibility();
        setAorFilterIds(selfAndDescendantIds);
        return;
      }

      if (!aorFilterIds || aorFilterIds.length === 0) {
        return;
      }

      setAorFilterIds([...new Set([...aorFilterIds, ...selfAndDescendantIds])]);
    },
    [
      allAorIds,
      aorFilterIds,
      isAllVisible,
      isAorVisible,
      selfAndDescendantIds,
      setAorFilterIds,
      toggleAorVisibility,
    ]
  );

  const handleGoTo = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      if (!item.currentLocationGeom) return;

      try {
        if (!isAllVisible) handleToggleVisibility(e);
        await zoomToAorById(item.id, {
          filterIds: selfAndDescendantIds,
          ensureVisible: true,
        });
      } catch (error) {
        console.error('Error zooming to AOR:', error);
      }
    },
    [
      handleToggleVisibility,
      isAllVisible,
      item.id,
      item.currentLocationGeom,
      selfAndDescendantIds,
      zoomToAorById,
    ]
  );

  const visibilityCheckbox = isMapVisible ? (
    <Checkbox
      checked={isAllVisible}
      indeterminate={isIndeterminate}
      className="tw:shrink-0"
      onClick={(event) => event.stopPropagation()}
      onCheckedChange={() => handleToggleVisibility()}
      aria-label={
        isAllVisible ? `Hide ${itemName} from map` : `Show ${itemName} on map`
      }
    />
  ) : null;

  return (
    <Card
      size="sm"
      className={cn('dark tw:gap-0 tw:py-0 tw:text-foreground', marginClass)}
      data-tour={cardTour}
      data-open={open ? 'true' : undefined}
    >
      <Collapsible open={open} onOpenChange={setOpen}>
        <div
          role="button"
          tabIndex={0}
          aria-expanded={open}
          className="tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:px-3 tw:py-2 tw:transition-colors tw:hover:bg-muted/40"
          onClick={() => setOpen((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setOpen((prev) => !prev);
            }
          }}
        >
          <ChevronDown
            className={cn(
              'tw:h-4 tw:w-4 tw:shrink-0 tw:text-muted-foreground tw:transition-transform',
              open && 'tw:rotate-180'
            )}
          />
          {visibilityCheckbox}
          <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm tw:font-medium">
            {itemName}
          </span>
          {isMapVisible && (
            <div
              className="tw:shrink-0"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
              role="presentation"
            >
              <HierarchyItemCardActions
                name={itemName}
                onGoTo={handleGoTo}
                tour={mapTour}
              />
            </div>
          )}
        </div>
        <CollapsibleContent className="tw:min-w-0 tw:overflow-hidden">
          <div
            className="tw:space-y-2 tw:border-t tw:border-border tw:p-3"
            data-tour={detailsTour}
          >
            <HierarchyItemCardBody
              aorId={item.id}
              aorName={itemName}
              aorSitrep={aorSitrep}
              onViewSitStat={handleViewSitStat}
              onAction={onAction}
              actionLabel={actionLabel}
              noActionMessage={noActionMessage}
              actionTour={actionTour}
              forceAssetsOpen={forceAssetsOpen}
            >
              {children}
            </HierarchyItemCardBody>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
